import { Announcement } from './Announcement';
import { Log } from '../../AuxillaryService/Log';
import { ReachAPI } from '../../ReachApi';
import { Permission, platform } from '../../Platform';
import { JSONPath } from 'jsonpath-plus';
import { AnnouncementController } from './AnnouncementController';
import { AnnouncementWidget } from './AnnouncementWidget';
import { Deferred } from '../../Utility/Deferred';
import { glom } from '../../Signage/glom';
import { AnnouncementStatus, DEFAULT_BACKGROUND_COLOR } from './types';
import { shuffleArray } from '../../Utility/Shuffle';
import { isNullish } from '../../Utility/Nullish';
import { PlayTimes, parsePlayTimes } from './PlayTimes';
import { getMessageCode } from './getMessageCode';
import { toInt } from '../../Utility';
import { isLegacyAnnouncementManifest } from './typesLegacyManifest';
import {
    createAnnouncement,
    INSUFFICIENT_PLAYLIST_RIGHTS,
    UnknownManifest,
    VIDEO_NOT_SUPPORTED,
} from './AnnouncementFactory';

const FALSE = 'false' as const;

export class AnnouncementGroup {
    public constructor(controller: AnnouncementController, widget: AnnouncementWidget) {
        this.#controller = controller;
        this.#widget = widget;
    }

    #controller: AnnouncementController;

    /**
     * Manifest array of announcement objects
     * @TODO: make private
     */
    public manifest: Announcement[] = [];

    /**
     * last announcement order
     * Annoyingly named 'id', has nothing to do with id, but rather stores
     * the value of the previous `moveNext` returned announcement's order
     * @TODO: make private
     */
    public announcementsLastOrderId = 0;

    /**
     * the hash of the last query
     */
    #lastQuery = '0';

    /**
     * true if an announcement has changed
     */
    #changed = false;

    /**
     * true if first run
     */
    #firstRun = true;

    /**
     * widget object
     */
    readonly #widget: AnnouncementWidget;

    /**
     * deferred object to handle manifest query
     * @todo: type API response
     */
    #queryManifestDeferred?: Deferred<UnknownManifest>;

    /**
     * Returns valid color
     */
    public static getColorCode(value: string): string {
        const findPoundSign = value.search('#');
        if (findPoundSign !== -1) {
            return value.substring(findPoundSign, findPoundSign + 7);
        } else {
            return value;
        }
    }

    /**
     * if the group has changed
     */
    public get isChanged(): boolean {
        return this.#changed;
    }

    /**
     * resets the group has changed to false
     */
    resetChanged(): void {
        this.#changed = false;
    }

    /**
     * Create groupings so they cannot be shuffled
     */
    static createGroupings(playlistItems: Announcement[]): (Announcement | Announcement[])[] {
        const groupManifest: Record<string, Announcement[]> = {};
        const groupedPlaylistItems: (Announcement | Announcement[])[] = [];

        playlistItems.forEach((playlistItem) => {
            if (playlistItem.configuration.group_id) {
                const groupIdAsString = playlistItem.configuration.group_id.toString();

                if (Object.prototype.hasOwnProperty.call(groupManifest, groupIdAsString)) {
                    groupManifest[groupIdAsString].push(playlistItem);
                } else {
                    groupManifest[groupIdAsString] = [playlistItem];
                    groupedPlaylistItems.push(groupManifest[groupIdAsString]);
                }
            } else {
                groupedPlaylistItems.push(playlistItem);
            }
        });

        return groupedPlaylistItems;
    }

    /**
     * manifest information
     */
    async getManifest(): Promise<UnknownManifest> {
        if (this.#queryManifestDeferred) {
            return this.#queryManifestDeferred.promise;
        }

        this.#queryManifestDeferred = new Deferred();

        try {
            const apiSecurity = new ReachAPI.Security(glom.playerUuid, glom.securityToken);

            const getListFunction = this.#widget.config.is_new_media_library
                ? ReachAPI.announcement.getList
                : ReachAPI.announcement.getLegacyList;

            try {
                const result = await getListFunction(
                    this.#widget.config.facilityid,
                    this.#widget.config.screenid,
                    this.#lastQuery,
                    ['active'],
                    apiSecurity
                );

                this.#queryManifestDeferred.resolve(result);

                return result;
            } catch (request) {
                if (getMessageCode(request) === 'ILLEGAL_PARAMETER') {
                    const data = {
                        announcements: [
                            {
                                announcement_id: this.#widget.id,
                                announcement_order: 1,
                                announcement_name: 'Playlist Error',
                                date_start: '1970-01-01',
                                date_end: '2099-12-31',
                                ann_status: AnnouncementStatus.ACTIVE,
                                run_days: 'Sun,Mon,Tue,Wed,Thu,Fri,Sat',
                                run_stime: '12:00 AM',
                                run_etime: '11:59 PM',
                                ann_seconds: 10,
                                template: INSUFFICIENT_PLAYLIST_RIGHTS,
                                go_full_screen: FALSE,
                                screen_alert: null,
                                updated_timestamp: null,
                                width: null,
                                height: null,

                                background_color: DEFAULT_BACKGROUND_COLOR,
                                group_id: null,
                                media_library_item_id: null,
                            },
                        ],
                        hash: INSUFFICIENT_PLAYLIST_RIGHTS,
                    };

                    this.#queryManifestDeferred.resolve(data);

                    return data;
                } else {
                    const manifestError =
                        request instanceof Error
                            ? request
                            : new Error('Manifest retrieval failed.');

                    this.#queryManifestDeferred.reject(manifestError);

                    throw manifestError;
                }
            }
        } finally {
            this.#queryManifestDeferred = undefined;
        }
    }

    /**
     * provisions any necessary announcements while updating manifest
     */
    async processUpdateActions(): Promise<void> {
        await this.updateManifest();

        await Promise.all(this.manifest.map((announcement) => announcement.provision()));
    }

    /**
     * maps manifest json to proper object
     * returns undefined if the manifest returned by the server has not changed
     */
    processJsonResponse(manifestJson: UnknownManifest): undefined | Announcement[] {
        if (!isNullish(manifestJson.hash) && manifestJson.hash !== this.#lastQuery) {
            //renew last query
            this.#lastQuery = manifestJson.hash;

            const zoneElement = document.getElementById(`zone_${this.#widget.zoneId}`);

            let stageWidth, stageHeight;
            if (
                isLegacyAnnouncementManifest(
                    manifestJson,
                    this.#widget.config.is_new_media_library !== true
                )
            ) {
                stageWidth = manifestJson.stage_width;
                stageHeight = manifestJson.stage_height;
            }

            const width = toInt(
                stageWidth || this.#widget.config.width || zoneElement?.offsetWidth || NaN
            );
            const height = toInt(
                stageHeight || this.#widget.config.height || zoneElement?.offsetHeight || NaN
            );

            this.#controller.setWidgetScale(width, height);

            const elements: Announcement[] = [];

            if (this.#widget.config.filter_expression) {
                const filterExpression = this.#widget.config.filter_expression;

                manifestJson.announcements = manifestJson.announcements.filter((row) => {
                    // The function returns an empty array
                    // if the evaluated expression is false
                    const data = JSONPath({
                        path: filterExpression,
                        json: row,
                    });

                    return data.length > 0;
                });
            }

            if (this.#widget.config.transform_method) {
                try {
                    if (this.#widget.config.transform_method) {
                        //disable eslint otherwise it will change let to const
                        /* eslint-disable */
                        let transformMethod!: <T>(ann: Array<T>) => Array<T>;
                        /* eslint-enable */

                        eval('transformMethod=' + this.#widget.config.transform_method + ';');
                        manifestJson.announcements =
                            transformMethod(manifestJson.announcements) ||
                            manifestJson.announcements;
                    }
                } catch (e) {
                    //todo: change to Log.Error
                    console.error(e);
                }
            }

            const { uniqueAnnouncements, playTimesMap } = parseAnnouncementPlayTimes(
                manifestJson.announcements
            );

            for (const announcementConfiguration of uniqueAnnouncements) {
                try {
                    let announcementConfigurationShallowCopy;
                    const isVideoNotSupported =
                        !platform().hasPermission(Permission.PLAY_REACH_VIDEO) &&
                        announcementConfiguration.template === 'video';
                    if (isVideoNotSupported) {
                        Log.Warning('Video is not supported on this platform', null, __fileline);
                        announcementConfigurationShallowCopy = {
                            ...announcementConfiguration,
                            template: VIDEO_NOT_SUPPORTED,
                            go_full_screen: FALSE,
                            ann_seconds: 10,
                        };
                    } else {
                        announcementConfigurationShallowCopy = {
                            ...announcementConfiguration,
                        };
                    }

                    const annId = toInt(announcementConfigurationShallowCopy.announcement_id);
                    const announcement = createAnnouncement(
                        this.#widget,
                        announcementConfigurationShallowCopy,
                        playTimesMap.get(annId) ?? new PlayTimes(),
                        width,
                        height
                    );
                    elements.push(announcement);
                } catch (e) {
                    Log.Error(
                        'Failed to parse announcement: ' + String(e),
                        'SIGNAGE_ANNOUNCEMENT_PARSE_ERROR',
                        __fileline,
                        { error: e, announcement: announcementConfiguration }
                    );
                }
            }

            return elements;
        }

        return undefined;
    }

    /**
     * updates manifest
     */
    async updateManifest(): Promise<void> {
        try {
            const manifestXml = await this.getManifest();

            this.merge(this.processJsonResponse(manifestXml));
        } catch (error) {
            Log.Error('query manifest failed..', null, __fileline, error);
        }
    }

    /**
     * merges old manifest with new manifest info
     */
    merge(newManifest?: Announcement[]): void {
        if (!newManifest) {
            //no changes present on the server, return
            return;
        }

        const merged = [];

        for (let i = 0; i <= newManifest.length - 1; ++i) {
            //shift one off the new manifest array
            const newAnnouncement = newManifest[i];

            if (!newAnnouncement) {
                continue;
            }

            let found = false;
            for (let i = 0; i < this.manifest.length; ++i) {
                if (this.manifest[i].configuration.id === newAnnouncement.configuration.id) {
                    //found a match, update existing announcement with new
                    //announcement
                    const existingAnnouncement = this.manifest[i];

                    this.#changed = existingAnnouncement.update(newAnnouncement) || !this.#firstRun;

                    merged.push(existingAnnouncement);
                    found = true;

                    //slice this element out of the array!
                    this.manifest.splice(i, 1);

                    break;
                }
            }

            if (!found) {
                //new announcement was not found, add to merged
                this.#changed = !this.#firstRun;
                merged.push(newAnnouncement);
            }
        }

        //items left have not been modified
        this.manifest.forEach(function (announcement) {
            announcement.safeUnprovision().then();
        });

        this.manifest = merged.sort((a, b) => {
            return a.order() - b.order();
        });

        if (this.#widget.config.shuffle) {
            const shuffled = shuffleArray(
                AnnouncementGroup.createGroupings(this.manifest.slice())
            ).flat();

            this.manifest.length = 0;
            this.manifest.push(...shuffled);
        }

        //it is possible to have multiple announcements with the same order ID in
        //CMS, so we need to set them ourselves
        for (let i = 0; i < this.manifest.length; ++i) {
            this.manifest[i].order(i + 1);
        }

        Log.Info('CHANGES MERGED!', null, __fileline);

        this.#firstRun = false;
    }

    /**
     * sets all provision locks to false
     */
    public liftProvisionLocks(): void {
        this.manifest.forEach((announcement) => {
            announcement.provisionIsLocked = false;
        });
    }

    /**
     * Returns the next playable announcement and updates announcementsLastOrderId.
     * will return `undefined` if no announcements are playable.
     */
    public moveNext(): undefined | Announcement {
        const nextItem = this.peekNext();

        if (nextItem && !nextItem.configuration.alert) {
            this.announcementsLastOrderId = nextItem.order();
        }

        return nextItem;
    }

    /**
     * Returns the next playable announcement
     * will return `undefined` if no announcements are playable.
     */
    public peekNext(): undefined | Announcement {
        //Check to see if there are any alerts, which have priority
        const alerted = this.manifest.find(
            (announcement) => announcement.configuration.alert && announcement.canPlay()
        );

        //note: this only returns one alert announcement, if multiple are alerted,
        //only the first assigned announcement will be allowed
        if (alerted) {
            return alerted;
        }

        /**
         * Sort the manifest by order
         * @type {AnnouncementBase[]}
         */
        let sortedManifest = this.manifest.slice().sort((playlistItemA, playlistItemB) => {
            return playlistItemA.order() - playlistItemB.order();
        });

        const currentItemIndex = sortedManifest.findIndex(
            (playlistItem) => playlistItem.order() === this.announcementsLastOrderId
        );
        if (currentItemIndex !== -1) {
            //Order sortedManifest so that it begins with the current playlistItem and
            //automatically rolls around if there are no items with greater order
            //EX: currentItemIndex = 2 then [1, 2, 3, 4, 5] --> [4, 5, 1, 2, 3]
            const sliceAt =
                currentItemIndex >= sortedManifest.length - 1 ? 0 : currentItemIndex + 1;
            sortedManifest = sortedManifest.slice(sliceAt).concat(sortedManifest.slice(0, sliceAt));
        }

        for (let i = 0; i < sortedManifest.length; i++) {
            const playlistItem = sortedManifest[i];
            if (playlistItem.canPlay()) {
                return playlistItem;
            }
        }

        //No item found
        return undefined;
    }

    public async deprovision(): Promise<void> {
        const stopPromises = this.manifest.map((announcement) => announcement.halt());
        await Promise.all(stopPromises);
    }
}

type AnyAnnouncements = UnknownManifest['announcements'];

/**
 * The announcements coming from the api will not be unique if they have advanced scheduling. Each
 * entry for advanced schedules will have it’s own announcement entry, making it so there will be
 * multiple announcements with the same ID.
 *
 * This function returns an filtered array of unique announcements and a Map containing each unique
 * announcement's {@link PlayTimes}.
 */
function parseAnnouncementPlayTimes(announcements: AnyAnnouncements): {
    uniqueAnnouncements: AnyAnnouncements;
    playTimesMap: ReturnType<typeof parsePlayTimes>;
} {
    const playTimesMap = parsePlayTimes(
        announcements.map((a) => {
            return {
                announcement_id: a.announcement_id,
                run_days: a.run_days,
                run_stime: a.run_stime,
                run_etime: a.run_etime,
            };
        })
    );

    const knownIds = new Set<number>();
    const uniqueAnnouncements = announcements.filter((annConfig) => {
        const annId = parseInt(String(annConfig.announcement_id));
        if (knownIds.has(annId)) {
            return false;
        }

        knownIds.add(annId);
        return true;
    });

    return { uniqueAnnouncements, playTimesMap };
}
